import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ShoppingBag, RefreshCw, Clock, CheckCircle, XCircle, Loader, Truck } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { useMyOrders } from '@/hooks/useMyOrders'
import { formatRupiah } from '@/lib/utils'
import Skeleton from '@/components/Skeleton'
import { ORDER_STATUS } from '@/types'
import type { Order, OrderStatusId } from '@/types'

const TABS = [
  { key: 0, label: 'Semua' },
  { key: 1, label: 'Menunggu' },
  { key: 2, label: 'Dikonfirmasi' },
  { key: 3, label: 'Selesai' },
  { key: 4, label: 'Dibatalkan' },
] as const

type Tab = 0 | OrderStatusId

const STATUS_ICON: Record<OrderStatusId, React.ReactNode> = {
  1: <Clock size={12} />,
  2: <Loader size={12} />,
  3: <CheckCircle size={12} />,
  4: <XCircle size={12} />,
  5: <Truck size={12} />,
  6: <CheckCircle size={12} />,
}

function filterByTab(orders: Order[], tab: Tab): Order[] {
  if (tab === 0) return orders
  return orders.filter(o => o.status_id === tab)
}

function OrderCard({ order }: { order: Order }) {
  const navigate = useNavigate()
  const meta = ORDER_STATUS[order.status_id]

  const tanggal = new Date(order.tanggal_kirim).toLocaleDateString('id-ID', {
    day: 'numeric', month: 'long', year: 'numeric',
  })

  return (
    <div className="bg-white rounded-xl shadow-sm p-4 space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-xs text-gray-400 font-mono">{order.nomor_order ?? '—'}</p>
          <p className="font-semibold text-gray-800 text-sm mt-0.5">{order.nama_produk}</p>
        </div>
        <span className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full font-medium shrink-0 ${meta.color}`}>
          {STATUS_ICON[order.status_id]}
          {meta.label}
        </span>
      </div>

      <div className="flex items-center justify-between text-sm text-gray-500 border-t pt-3">
        <div className="space-y-0.5">
          <p>{order.qty} × {formatRupiah(order.harga_satuan)}</p>
          <p className="text-xs text-gray-400">{tanggal}{order.jam_kirim ? ` · ${order.jam_kirim}` : ''}</p>
        </div>
        <p className="font-semibold text-gray-800">{formatRupiah(order.subtotal)}</p>
      </div>

      <div className="flex items-center gap-2 pt-1">
        <Link
          to={`/status?order_id=${order.id}`}
          className="flex-1 text-center text-xs py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
        >
          Lihat Detail
        </Link>
        {order.status_id === 3 && order.produk_id && (
          <button
            onClick={() => navigate(`/order?produk_id=${order.produk_id}`)}
            className="flex-1 flex items-center justify-center gap-1.5 text-xs py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors font-medium"
          >
            <RefreshCw size={12} />
            Order Lagi
          </button>
        )}
      </div>
    </div>
  )
}

export default function MyOrder() {
  const { user } = useAuth()
  const { orders, loading } = useMyOrders(user?.id)
  const [tab, setTab] = useState<Tab>(0)

  if (!user) {
    return (
      <div className="max-w-lg mx-auto px-4 py-16 text-center">
        <ShoppingBag size={40} className="mx-auto text-gray-300 mb-3" />
        <p className="text-gray-500 text-sm mb-4">Masuk untuk melihat riwayat pesanan kamu.</p>
        <Link
          to="/login"
          className="inline-block px-6 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-xl hover:bg-blue-700 transition-colors"
        >
          Masuk
        </Link>
      </div>
    )
  }

  const filtered = filterByTab(orders, tab)

  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      <h1 className="text-lg font-semibold text-gray-800 mb-4">Pesanan Saya</h1>

      {/* Filter dropdown */}
      <div className="mb-4">
        <select
          value={tab}
          onChange={e => setTab(Number(e.target.value) as Tab)}
          className={`w-full px-3 py-2 rounded-xl border text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
            tab === 4
              ? 'border-red-300 text-red-600 bg-red-50'
              : 'border-gray-200 text-gray-700 bg-white'
          }`}
        >
          {TABS.map(t => (
            <option key={t.key} value={t.key}>
              {t.label}
              {t.key !== 0 && !loading && filterByTab(orders, t.key).length > 0
                ? ` (${filterByTab(orders, t.key).length})`
                : ''}
            </option>
          ))}
        </select>
      </div>

      {/* Content */}
      {loading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-36 rounded-xl" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16">
          <ShoppingBag size={36} className="mx-auto text-gray-300 mb-2" />
          <p className="text-sm text-gray-400 mb-3">Belum ada pesanan.</p>
          <Link
            to="/kategori/semua"
            className="inline-block px-5 py-2 bg-blue-600 text-white text-sm font-medium rounded-xl hover:bg-blue-700 transition-colors"
          >
            Lihat Produk
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(o => <OrderCard key={o.id} order={o} />)}
        </div>
      )}
    </div>
  )
}
